import React from 'react';
import { Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase-config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SecurityLog from './SecurityLog';

const S = {
  shell: { display: 'flex', minHeight: '100vh', background: '#080e1a' },
  sidebar: {
    width: '220px', flexShrink: 0,
    background: '#0a1628',
    borderRight: '1px solid rgba(0,150,255,0.1)',
    display: 'flex', flexDirection: 'column',
    padding: '0',
  },
  sidebarTop: {
    padding: '20px 20px 12px',
    borderBottom: '1px solid rgba(0,150,255,0.1)',
  },
  brand: { color: '#e0eaff', fontWeight: 800, letterSpacing: '0.06em', fontSize: '0.8rem' },
  brandSub: { color: 'rgba(120,170,240,0.55)', fontSize: '0.65rem', letterSpacing: '0.08em', marginTop: '2px' },
  nav: { padding: '16px 0', flex: 1, overflowY: 'auto' },
  section: { padding: '6px 20px 4px', color: 'rgba(100,140,200,0.5)', fontSize: '0.6rem', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 },
  navLink: {
    display: 'block', padding: '8px 20px',
    color: 'rgba(180,210,255,0.75)', fontSize: '0.8rem',
    textDecoration: 'none', letterSpacing: '0.02em',
    transition: 'background 0.15s, color 0.15s',
    borderLeft: '2px solid transparent',
  },
  navLinkHover: { background: 'rgba(0,150,255,0.08)', color: '#e0eaff', borderLeftColor: 'rgba(0,170,255,0.6)' },
  sidebarBottom: { padding: '16px 20px', borderTop: '1px solid rgba(0,150,255,0.1)' },
  signOutBtn: {
    width: '100%', background: 'rgba(255,80,80,0.1)', color: '#ff7070',
    border: '1px solid rgba(255,80,80,0.25)', borderRadius: '6px',
    padding: '7px 0', cursor: 'pointer', fontSize: '0.78rem',
    letterSpacing: '0.04em',
  },
  userBlock: {
    padding: '0 0 12px',
    borderBottom: '1px solid rgba(0,150,255,0.07)',
    marginBottom: '12px',
  },
  userEmail: { color: 'rgba(140,185,240,0.85)', fontSize: '0.72rem', wordBreak: 'break-all' },
  userRole: {
    display: 'inline-block', marginTop: '5px',
    padding: '2px 8px', borderRadius: '4px',
    background: 'rgba(0,150,255,0.12)', color: '#7eb8f7',
    fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700,
  },
  main: { flex: 1, padding: '32px 36px', overflowY: 'auto' },
  pageTitle: { color: '#e0eaff', fontWeight: 800, fontSize: '1.5rem', letterSpacing: '0.04em', marginBottom: '24px' },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '28px' },
  card: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,150,255,0.12)',
    borderRadius: '10px', padding: '20px',
  },
  cardLabel: { color: 'rgba(140,185,240,0.7)', fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase' },
  cardValue: { color: '#7eb8f7', fontSize: '2rem', fontWeight: 800, marginTop: '8px' },
  activityCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,150,255,0.12)',
    borderRadius: '10px', padding: '24px',
  },
  activityTitle: { color: '#e0eaff', fontWeight: 700, fontSize: '1rem', marginBottom: '12px' },
  activityText: { color: 'rgba(140,185,240,0.6)', fontSize: '0.82rem' },
};

const NavLink = ({ to, children, external }) => {
  const [hovered, setHovered] = React.useState(false);
  const style = { ...S.navLink, ...(hovered ? S.navLinkHover : {}) };
  if (external) {
    return <a href={to} target="_blank" rel="noreferrer" style={style} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>{children}</a>;
  }
  return <Link to={to} style={style} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>{children}</Link>;
};

function DashboardHome() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  async function handleSignOut() {
    await signOut(auth);
    navigate('/login');
  }

  return (
    <div style={S.shell}>
      {/* ── Sidebar ── */}
      <aside style={S.sidebar}>
        <div style={S.sidebarTop}>
          <div style={S.brand}>RAMDESIGNWORKS</div>
          <div style={S.brandSub}>Operator Panel</div>
        </div>

        <nav style={S.nav}>
          <div style={S.section}>Platform</div>
          <NavLink to="/">Home</NavLink>
          <NavLink to="/dashboard">Dashboard</NavLink>
          <NavLink to="/analytics">Analytics</NavLink>
          <NavLink to="/data">Data</NavLink>
          <NavLink to="/billing">Billing</NavLink>
          <NavLink to="/account">Account Settings</NavLink>

          <div style={{ ...S.section, marginTop: '12px' }}>Site Pages</div>
          <NavLink to="/discover">The Mirror</NavLink>
          <NavLink to="/solutions">Solutions</NavLink>
          <NavLink to="/build-for-me">Build For Me</NavLink>

          <div style={{ ...S.section, marginTop: '12px' }}>Admin Tools</div>
          <NavLink to="/pages/HR.html" external>HR Portal</NavLink>
          <NavLink to="/pages/admin_portal.html" external>Admin Portal</NavLink>
          <NavLink to="/pages/staff.html" external>Staff</NavLink>
          <NavLink to="/pages/internal_dashboard.html" external>Inventory Dashboard</NavLink>
          <NavLink to="/pages/data_viewer.html" external>Data Viewer</NavLink>
          <NavLink to="/pages/diagnostics.html" external>Diagnostics</NavLink>
        </nav>

        <div style={S.sidebarBottom}>
          <div style={S.userBlock}>
            <div style={S.userEmail}>{currentUser?.email}</div>
            <span style={S.userRole}>{userProfile?.role || 'staff'}</span>
          </div>
          <button onClick={handleSignOut} style={S.signOutBtn}>Sign Out</button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main style={S.main}>
        <div style={S.pageTitle}>Dashboard</div>

        <div style={S.grid4}>
          <div style={S.card}>
            <div style={S.cardLabel}>Total Views</div>
            <div style={S.cardValue}>--</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Unique Visitors</div>
            <div style={S.cardValue}>--</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Inquiries</div>
            <div style={S.cardValue}>--</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>This Month</div>
            <div style={S.cardValue}>$--</div>
          </div>
        </div>

        <div style={S.activityCard}>
          <div style={S.activityTitle}>Recent Activity</div>
          <div style={S.activityText}>Coming soon...</div>
        </div>

        <SecurityLog />
      </main>
    </div>
  );
}

export default DashboardHome;
